import { Formik, Form } from 'formik';
import { MFTextField } from '../../lib/formik';
import { MFCheckbox } from '../../lib/formik/Checkbox';
import { ProceedSaveLater } from './components';
import { useDispatch, useSelector } from 'react-redux';
import { RationaleQuestions } from '../../redux-store/actions';
import { RootStateType } from '../../redux-store/reducers';
import {
  applicationComparison,
  checkValidationBasedOnDate,
  saveForLater,
} from '../../utils/utilityFunctions';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
} from '@mui/material';
import { useEffect } from 'react';
import React from 'react';
import { APPLICATION_TYPE } from '../../utils/constant';
import { useFeatureToggles } from '../../utils/useDataMdms';

// The Excuse & Exclusion answers are a flat map of string fields while editing.
// The authoritative field keys live on ApplicationProps.
export type Values = { [key: string]: string };

export type RationaleColumnType = {
  key: string;
  header: string;
  type: 'checkbox' | 'text' | 'remarks' | 'label';
};

export type RationaleQuestionType = {
  id: string;
  applicableKey: string;
  stocksKey: string;
  applicable?: string;
  rationale: string;
  stocks?: string;
  mandatoryEnclosures: string;
  [key: string]: string | undefined;
};

export type RationaleSectionType = {
  section: string;
  columns: RationaleColumnType[];
  questions: RationaleQuestionType[];
};

export type ExcuseValues = {
  excuse?: Values;
  rationaleQue: RationaleSectionType[];
  saveType: 'save for later' | 'save and proceed';
  [key: string]: Values | RationaleSectionType[] | string | undefined;
};

// The stocks text field is only shown/relevant when the checkbox is ticked.
export const isRowApplicable = (q: RationaleQuestionType, values: ExcuseValues): boolean => {
  const val = values.excuse?.[q.applicableKey];
  return val === 'true';
};

export const _initialExcuseValues: Values = {
  isInvestmentViolation: '',
  isInvestmentViolationStocks: '',
  isPolicyContravention: '',
  isPolicyContraventionStocks: '',
};

// Application-level field keys that hold the Excuse & Exclusion answers.
export const EXCUSE_EXCLUSION_FIELD_KEYS = Object.keys(_initialExcuseValues);

// Extracts the Excuse & Exclusion fields from a (flat) application object.
// Applicable (checkbox) keys — these must be normalized to 'true' or '' so a
// legacy/string value like 'false' never renders as a checked box.
const EXCUSE_APPLICABLE_KEYS = ['isInvestmentViolation', 'isPolicyContravention'];

export const pickExcuseFields = (application?: Partial<Record<string, unknown>> | null): Values => {
  const picked: Values = {};
  if (!application) return picked;
  EXCUSE_EXCLUSION_FIELD_KEYS.forEach((key) => {
    const val = application[key];
    if (val === undefined) return;
    if (EXCUSE_APPLICABLE_KEYS.includes(key)) {
      // Only true/'true' counts as checked; anything else is treated as unchecked.
      picked[key] = val === true || val === 'true' ? 'true' : '';
    } else {
      picked[key] = val as string;
    }
  });
  return picked;
};

export type _Props = {
  values: ExcuseValues;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

export const ExcuseExclusionForm: React.FC<_Props> = ({ values, handleChange, setFieldValue }) => {
  return (
    <Box>
      {values.rationaleQue.map((section: RationaleSectionType, i: number) => {
        const [applicableCol, rationaleCol, stocksCol, enclosuresCol] = section.columns;
        return (
          <Box key={i} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {section.section}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                      {applicableCol?.header ?? 'Please ✓ as applicable'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>
                      {rationaleCol?.header ?? 'Rationale'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                      {stocksCol?.header ?? 'Name of Stocks / Securities'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                      {enclosuresCol?.header ?? 'Mandatory Enclosures'}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {section.questions.map((q) => {
                    const showStocks = isRowApplicable(q, values);
                    return (
                      <TableRow key={q.id}>
                        {/* Column 1: non-mandatory checkbox. Controlled strictly
                            by the boolean `showStocks`; stores explicit
                            'true'/'' strings so the value can never render as a
                            stale checked state. */}
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <MFCheckbox
                            name={`excuse.${q.applicableKey}`}
                            label=""
                            checked={showStocks}
                            onChange={(e) => {
                              const isChecked = (e.target as HTMLInputElement).checked;
                              setFieldValue(`excuse.${q.applicableKey}`, isChecked ? 'true' : '');
                              if (!isChecked) {
                                // Clear the stocks value when unchecking.
                                setFieldValue(`excuse.${q.stocksKey}`, '');
                              }
                            }}
                          />
                        </TableCell>

                        {/* Column 2: rationale text shown as-is */}
                        <TableCell sx={{ verticalAlign: 'top' }}>{q.rationale}</TableCell>

                        {/* Column 3: stocks/securities text field, only when checked */}
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {showStocks && (
                            <MFTextField
                              name={`excuse.${q.stocksKey}`}
                              value={values.excuse?.[q.stocksKey] ?? ''}
                              onChange={handleChange}
                              placeholder="Enter Name of Stocks / Securities"
                              multiline
                              minRows={2}
                            />
                          )}
                        </TableCell>

                        {/* Column 4: mandatory enclosures label shown as-is */}
                        <TableCell sx={{ verticalAlign: 'top' }}>{q.mandatoryEnclosures}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};

export default function ExcuseExclusionScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { toggler } = useFeatureToggles();
  const { application } = useSelector((store: RootStateType) => store.application);
  const {
    auth: { role },
  } = useSelector((store: RootStateType) => store);
  const [rationaleJson, setRationaleJson] = React.useState<RationaleSectionType[]>([]);
  const [initialExcuseValues, setInitialExcuseValues] = React.useState<ExcuseValues>({
    excuse: _initialExcuseValues,
    rationaleQue: [] as unknown as RationaleSectionType[],
    saveType: 'save for later',
  });

  useEffect(() => {
    async function fetchRationaleData() {
      setLoading(true);
      try {
        const res = ((await dispatch(RationaleQuestions())) ||
          []) as unknown as RationaleSectionType[];
        setRationaleJson(res);
      } catch (error) {
        console.error('Error fetching Excuse & Exclusion data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchRationaleData();
  }, []);

  useEffect(() => {
    setInitialExcuseValues((prev) => ({
      ...prev,
      // Excuse & Exclusion answers live directly on the application (BE contract).
      excuse: {
        ..._initialExcuseValues,
        ...pickExcuseFields(application),
      } as Values,
      rationaleQue: rationaleJson,
    }));
  }, [application, rationaleJson]);

  function buildExcusePayloadForBE(values: ExcuseValues, json: RationaleSectionType[]) {
    // eslint-disable-next-line
    const payload: any = {};
    json.forEach((section) => {
      section.questions.forEach((q) => {
        const applicableVal = values.excuse?.[q.applicableKey];
        const isChecked = applicableVal === 'true';
        payload[q.applicableKey] = isChecked ? 'true' : '';
        payload[q.stocksKey] = isChecked ? values.excuse?.[q.stocksKey] ?? '' : '';
      });
    });
    return payload;
  }

  const handleSubmit = async (values: ExcuseValues) => {
    try {
      const {
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationType,
        createdAt = '',
      } = application || {};
      const { saveType } = values;

      // Merge the Excuse & Exclusion answers directly onto the application.
      const updatedPayload = buildExcusePayloadForBE(values, initialExcuseValues.rationaleQue);
      const qibchecklistset = checkValidationBasedOnDate(
        createdAt,
        toggler.dateFeatureToggles.qibChecklistValidationDate
      )
        ? 1
        : 0;
      const isSaveLater = saveType !== 'save and proceed';
      const nextSlug = 'document-details';
      const checkApplication = applicationComparison(application, {
        ...application,
        ...updatedPayload,
        currentStep:
          applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
            ? !!currentStep && currentStep > 6
              ? currentStep
              : Number(currentStep) + 1
            : !!currentStep && currentStep > 9 + qibchecklistset
            ? currentStep
            : Number(currentStep) + 1,
      });

      if (id) {
        if (!isSaveLater || !checkApplication) {
          setLoading(true);
          await dispatch(
            updateApplication({
              body: {
                ...application,
                ...updatedPayload,
                currentStep:
                  applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? 5 + qibchecklistset : 10,
              },
              applicationId: id,
              ...(isSaveLater && { toastMessage: '' }),
            })
          );
        } else if (isSaveLater) {
          enqueueSnackbar(
            `Application ${application?.applicationNumber} - ` + ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        saveType === 'save and proceed'
          ? history.push(nextSlug, { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik initialValues={initialExcuseValues} enableReinitialize={true} onSubmit={handleSubmit}>
      {({ values, handleChange, setValues, setFieldValue }) => (
        <Form>
          <ExcuseExclusionForm
            values={values}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
          />
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Form>
      )}
    </Formik>
  );
}
